import {
  TelegramPostPreviewProps,
  PreviewMessageType,
} from './TelegramPostPreview.model';
import styles from './TelegramPostPreview.module.scss';
import { FlexRow, FlexCell, Text, Panel, Switch } from '@epam/promo';
import { useState } from 'react';
import { parseTelegramTokens } from './parseTelegramTokens';
import { PreviewPanel } from 'components/common/PreviewPanel/PreviewPanel';
import { ReactComponent as NotificationErrorFillIcon } from '@epam/assets/icons/notification-error-fill.svg';
import { ReactComponent as NotificationDoneFillIcon } from '@epam/assets/icons/notification-done-fill.svg';

const MessageBubbleTail = () => {
  return (
    <svg
      aria-hidden="true"
      className={styles['bubble-tail']}
      height="18"
      viewBox="0 0 25 18"
      width="25"
      xmlns="http://www.w3.org/2000/svg">
      <path
        d="M14 1
          Q18 8 25 10
          Q20 18 1 20
          Q14 20 14 1
          Z"
      />
    </svg>
  );
};

const PostPreview = ({
  title,
  message,
}: {
  title: string;
  message: string;
}) => {
  return title || message ? (
    <div className={styles.bubble}>
      <Text
        color="white"
        cx={styles['post-title']}
        fontSize="14"
        fontWeight="700"
        lineHeight="24"
        size="18">
        {title}
      </Text>
      <Text
        color="white"
        cx={styles['post-text']}
        fontSize="14"
        lineHeight="19"
        size="18">
        {parseTelegramTokens(message)}
      </Text>
      <MessageBubbleTail />
    </div>
  ) : null;
};

export const TelegramPostPreview = ({
  channelName = 'Community Onboarding',
  title,
  message,
  avatarUrl, // TODO: set correct avatar image as default value
  attachments,
  previewMessages,
}: TelegramPostPreviewProps) => {
  const [isAnswerShown, setIsAnswerShown] = useState(false);

  const validPreviewMessages =
    previewMessages?.filter((msg) => msg.text && msg.text.trim().length > 0) ??
    [];
  const hasAnswer = validPreviewMessages.length > 0;

  const attachmentsPreview = () => {
    if (attachments?.length) {
      return (
        <FlexRow
          cx={styles['post-attachments-container']}
          padding="18"
          rowGap="12"
          vPadding="36">
          {attachments.flatMap((attachment, index) => {
            if (!attachment.type.startsWith('image/')) {
              return [];
            }

            // Local File (create/upload flow) → object URL;
            // server attachment (loaded broadcast) → presigned url, which may be absent.
            const src =
              attachment instanceof File
                ? URL.createObjectURL(attachment)
                : attachment.url;

            if (!src) {
              return [];
            }

            return (
              <FlexCell
                key={attachment instanceof File ? index : attachment.id}>
                <img
                  alt={attachment.name}
                  className={styles['post-attachment']}
                  src={src}
                />
              </FlexCell>
            );
          })}
        </FlexRow>
      );
    }
  };

  const answerToggle = hasAnswer ? (
    <FlexRow alignItems="center" columnGap="6">
      <Text color="gray90" fontSize="14">
        Show answer
      </Text>
      <Switch value={isAnswerShown} onValueChange={setIsAnswerShown} />
    </FlexRow>
  ) : null;

  const previewMessageBubbles =
    hasAnswer && isAnswerShown
      ? validPreviewMessages.map((msg, index) => {
          const isError = msg.type === PreviewMessageType.ERROR;
          const Icon = isError
            ? NotificationErrorFillIcon
            : NotificationDoneFillIcon;
          const iconClass = isError
            ? styles['error-icon']
            : styles['success-icon'];
          const trimmedText = msg.text!.trim();

          return (
            <div key={index} className={styles['message-bubble']}>
              <FlexRow alignItems="flex-start" columnGap={8}>
                <Icon className={`${styles['message-icon']} ${iconClass}`} />
                <Text
                  color="white"
                  cx={styles['message-text']}
                  fontSize="14"
                  lineHeight="19"
                  size="18">
                  {parseTelegramTokens(trimmedText)}
                </Text>
              </FlexRow>
              <MessageBubbleTail />
            </div>
          );
        })
      : null;

  return (
    <PreviewPanel headerAction={answerToggle}>
      <Panel cx={styles.wrapper}>
        <FlexRow
          alignItems="center"
          columnGap="12"
          cx={styles.header}
          justifyContent="start"
          padding="12">
          <img className={styles.avatar} src={avatarUrl} />
          <FlexCell grow={1}>
            <Text color="white" fontSize="16" fontWeight="600" size="18">
              {channelName}
            </Text>
            <Text cx={styles.handle} fontSize="14" size="18">
              bot
            </Text>
          </FlexCell>
        </FlexRow>
        <div className={styles.content}>
          <PostPreview message={message} title={title} />
          {attachmentsPreview()}
          {previewMessageBubbles}
        </div>
      </Panel>
    </PreviewPanel>
  );
};
